import React from 'react';
import {
  type BulkDraft,
  type BulkDraftPageData,
  getBulkDraftPage,
  getSentBatch
} from '~/api/data/email-bulk-send/queries';
import { isBulkSendDrafting } from '~/components/mailbox/outbox-bulk-send-rows';
import { removeRecipient } from './bulk-draft-remove-recipient';
import { useBatchProgressPolling } from './use-batch-progress-polling';

/** How often the page reads the bulk draft again while Cara is drafting it. */
const DRAFTING_POLL_MS = 15_000;
/** How often it reads it otherwise, so a drafting run started from chat still shows. */
const SETTLED_POLL_MS = 60_000;

/**
 * How long after the composer closes the page reads again: the editor sends its last edit as it
 * closes, and the composer's save queue may still be running.
 */
const EDIT_SAVE_SETTLE_MS = 3_000;

/**
 * Numbers reads as they start, so only an answer newer than the one shown replaces it: a read
 * that started before a Remove can't bring the row back.
 */
const useReadOrder = () => {
  const started = React.useRef(0);
  const shown = React.useRef(0);
  return React.useMemo(
    () => ({
      start: () => ++started.current,
      /** Whether this read's answer is the newest yet; marks it shown if so. */
      claim: (read: number) => {
        if (read <= shown.current) return false;
        shown.current = read;
        return true;
      }
    }),
    []
  );
};

const withoutId = (ids: ReadonlySet<string>, removed: string) =>
  new Set([...ids].filter((id) => id !== removed));

const withoutErrorFor = (errorById: ReadonlyMap<string, string>, recipientId: string) =>
  new Map([...errorById].filter(([id]) => id !== recipientId));

/**
 * The bulk draft the route loaded, read again so its rows move to Ready while Cara drafts:
 * often while she does, slowly otherwise, and whenever the agent comes back to the window.
 * Once sent, only its batch is read again, as often as its progress can change.
 * Not a loader revalidation: that would swap the page for its loading state on every read.
 */
export const useLiveBulkDraftPage = (
  loadedPage: BulkDraftPageData
): {
  page: BulkDraftPageData;
  showBulkDraft: (bulkDraft: BulkDraft) => void;
  refreshAfterEdit: () => void;
  /** Shows the page sent, read again from scratch, or as of `sentAt` if that read fails. */
  showSent: (sentAt: BulkDraft['sentAt']) => Promise<void>;
  reloadBatch: () => void;
  /** Reads the page again from scratch, as after the batch went back to a draft. */
  reloadPage: () => void;
  /** Takes the row off at once, and puts it back with why if the server refuses. */
  removeRecipientNow: (recipientId: string) => void;
  /** Why the last Remove of each recipient failed. */
  removeErrorById: ReadonlyMap<string, string>;
} => {
  const [page, setPage] = React.useState(loadedPage);
  React.useEffect(() => setPage(loadedPage), [loadedPage]);

  // The bulk draft and its batch are read on their own schedules, so each keeps its own order.
  const draftReads = useReadOrder();
  const batchReads = useReadOrder();

  const isMounted = React.useRef(true);
  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const bulkSendId = page.bulkDraft?.id;
  const read = React.useCallback(
    async (options: { isFresh?: boolean } = {}) => {
      if (!bulkSendId) return;
      const draftRead = draftReads.start();
      const batchRead = batchReads.start();
      try {
        const { data, error } = await getBulkDraftPage(bulkSendId, options);
        if (!isMounted.current || !data.bulkDraft) return;
        const isDraftShown = draftReads.claim(draftRead);
        // A batch that failed to read keeps the one last shown.
        const isBatchShown = !!data.batch && batchReads.claim(batchRead);
        // Cancel can make the batch a bulk draft again, which has no batch.
        const isDraftAgain = isDraftShown && !data.bulkDraft.sentAt;
        setPage((last) => {
          // Emails that failed to read keep their last subjects and templates.
          const draftPart = error ? { ...last, bulkDraft: data.bulkDraft } : data;
          return {
            ...(isDraftShown ? draftPart : last),
            batch: isDraftAgain ? null : isBatchShown ? data.batch : last.batch
          };
        });
      } catch {
        // The page keeps what it last read; the next read tries again.
      }
    },
    [bulkSendId, draftReads, batchReads]
  );

  const readBatch = React.useCallback(async () => {
    if (!bulkSendId) return;
    const batchRead = batchReads.start();
    try {
      const { data: batch } = await getSentBatch(bulkSendId);
      if (isMounted.current && batch && batchReads.claim(batchRead)) {
        setPage((last) => ({ ...last, batch }));
      }
    } catch {
      // The page keeps the batch it last read; the next read tries again.
    }
  }, [bulkSendId, batchReads]);
  const reloadBatch = React.useCallback(() => void readBatch(), [readBatch]);
  const reloadPage = React.useCallback(() => void read({ isFresh: true }), [read]);
  useBatchProgressPolling(page.batch, reloadBatch);

  const isDrafting = !!page.bulkDraft && isBulkSendDrafting(page.bulkDraft);
  const isSent = !!page.bulkDraft?.sentAt;
  React.useEffect(() => {
    if (!bulkSendId || isSent) {
      return;
    }
    const pollMs = isDrafting ? DRAFTING_POLL_MS : SETTLED_POLL_MS;
    let isStopped = false;
    let isReading = false;
    let timeoutId: number | undefined;
    // The next read waits for this one, so reads never pile up.
    const readThenWait = async () => {
      window.clearTimeout(timeoutId);
      isReading = true;
      await read();
      isReading = false;
      if (!isStopped) {
        timeoutId = window.setTimeout(() => void readThenWait(), pollMs);
      }
    };
    const readOnFocus = () => {
      if (!isReading) {
        void readThenWait();
      }
    };
    timeoutId = window.setTimeout(() => void readThenWait(), pollMs);
    window.addEventListener('focus', readOnFocus);
    return () => {
      isStopped = true;
      window.clearTimeout(timeoutId);
      window.removeEventListener('focus', readOnFocus);
    };
  }, [bulkSendId, isDrafting, isSent, read]);

  const showBulkDraft = React.useCallback(
    (bulkDraft: BulkDraft) => {
      if (draftReads.claim(draftReads.start())) {
        setPage((last) => ({ ...last, bulkDraft }));
      }
    },
    [draftReads]
  );

  const showSent = React.useCallback(
    async (sentAt: BulkDraft['sentAt']) => {
      await read({ isFresh: true });
      // Send's own answer keeps the page sent when that read failed, or an older one lands late.
      if (isMounted.current && draftReads.claim(draftReads.start())) {
        setPage((last) =>
          last.bulkDraft && !last.bulkDraft.sentAt
            ? { ...last, bulkDraft: { ...last.bulkDraft, sentAt } }
            : last
        );
      }
    },
    [read, draftReads]
  );

  // Rows Remove took off, kept off even when a read that started before it still lists them.
  const [removedIds, setRemovedIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const [removeErrorById, setRemoveErrorById] = React.useState<ReadonlyMap<string, string>>(
    () => new Map()
  );
  const removeRecipientNow = React.useCallback(
    (recipientId: string) => {
      if (!bulkSendId) return;
      setRemovedIds((ids) => new Set(ids).add(recipientId));
      setRemoveErrorById((errors) => withoutErrorFor(errors, recipientId));
      const draftRead = draftReads.start();
      void removeRecipient(bulkSendId, recipientId).then((outcome) => {
        if (!isMounted.current) return;
        if ('failure' in outcome) {
          setRemovedIds((ids) => withoutId(ids, recipientId));
          setRemoveErrorById((errors) => new Map(errors).set(recipientId, outcome.failure));
          // A refusal can mean the page is behind, as when Cara started drafting the row.
          void read();
        } else if (draftReads.claim(draftRead)) {
          setPage((last) => ({ ...last, bulkDraft: outcome.bulkDraft }));
        }
      });
    },
    [bulkSendId, draftReads, read]
  );
  const shownPage = React.useMemo(() => {
    const { bulkDraft } = page;
    if (!bulkDraft || removedIds.size === 0) return page;
    const recipients = bulkDraft.recipients.filter(({ id }) => !removedIds.has(id));
    return { ...page, bulkDraft: { ...bulkDraft, recipients } };
  }, [page, removedIds]);

  const settleTimeoutId = React.useRef<number>(undefined);
  React.useEffect(() => () => window.clearTimeout(settleTimeoutId.current), []);
  const refreshAfterEdit = React.useCallback(() => {
    void read();
    window.clearTimeout(settleTimeoutId.current);
    settleTimeoutId.current = window.setTimeout(() => void read(), EDIT_SAVE_SETTLE_MS);
  }, [read]);

  return {
    page: shownPage,
    showBulkDraft,
    refreshAfterEdit,
    showSent,
    reloadBatch,
    reloadPage,
    removeRecipientNow,
    removeErrorById
  };
};
